import {
  SnapshotMonitor,
  SnapshotMarker,
  SnapshotMarkerDelta,
  SnapshotSpectralMask,
} from 'types';
import { SnapshotThreshold } from '../../../types/models/SnapshotThreshold';
import { parseTraceFromBuffer } from 'utils/traceUtils';

export const checkMonitorAlarming = (
  trace: number[],
  monitor: SnapshotMonitor
) => {
  const slicedTrace: number[] = trace.slice(monitor.begin, monitor.end);
  const maxAmplitude = Math.max(...slicedTrace);
  const minAmplitude = Math.min(...slicedTrace);

  if (
    maxAmplitude > monitor.upperbound / 1000.0 ||
    minAmplitude < monitor.lowerbound / 1000.0
  ) {
    return true;
  }

  return false;
};

export const checkThresholdAlarming = (
  trace: number[],
  threshold: SnapshotThreshold
) => {
  const slicedTrace: number[] = trace.slice(threshold.begin, threshold.end);
  const maxAmplitude = Math.max(...slicedTrace);
  const minAmplitude = Math.min(...slicedTrace);

  if (
    maxAmplitude > threshold.bound / 1000.0 &&
    minAmplitude < threshold.bound / 1000.0
  ) {
    return true;
  }

  return false;
};
export const checkMarkerDeltaAlarming = (
  trace: number[],
  markers: SnapshotMarker[],
  markerDelta: SnapshotMarkerDelta
) => {
  const { marker1_index, marker2_index, upperbound, lowerbound } = markerDelta;

  if (upperbound === 0 && lowerbound === 0) return false;

  const marker1 = markers.find((marker) => marker.index === marker1_index);
  const marker2 = markers.find((marker) => marker.index === marker2_index);

  if (!marker1 || !marker2) return false;

  const deltaValue = trace[marker2.x_pos] - trace[marker1.x_pos];

  if (deltaValue > upperbound / 1000.0 || deltaValue < lowerbound / 1000.0) {
    return true;
  }

  return false;
};

export const checkSpectralMaskAlarming = (
  trace: number[],
  baseline: number[],
  mask: SnapshotSpectralMask
) => {
  const { begin, end, upperbound, lowerbound } = mask;

  for (let i = begin; i <= end; i++) {
    const maskUpperLimit = baseline[i] + upperbound / 1000;
    const maskLowerLimit = baseline[i] - lowerbound / 1000;

    if (trace[i] > maskUpperLimit || trace[i] < maskLowerLimit) {
      return true;
    }
  }

  return false;
};

export const checkForAnyAlarm = (
  trace: string,
  baseline: string | undefined | null,
  monitors: SnapshotMonitor[] | undefined,
  markers: SnapshotMarker[] | undefined,
  markerDeltas: SnapshotMarkerDelta[] | undefined,
  masks: SnapshotSpectralMask[] | undefined,
  thresholds: SnapshotThreshold[] | undefined
) => {
  const parsedTrace = parseTraceFromBuffer(trace);

  if (monitors && monitors.length > 0) {
    for (let i = 0; i < monitors.length; i++) {
      const alarming = checkMonitorAlarming(parsedTrace, monitors[i]);
      if (alarming) return true;
    }
  }

  if (thresholds && thresholds.length > 0) {
    for (let i = 0; i < thresholds.length; i++) {
      const alarming = checkThresholdAlarming(parsedTrace, thresholds[i]);
      if (alarming) return true;
    }
  }

  if (markers && markerDeltas && markerDeltas.length > 0) {
    for (let i = 0; i < markerDeltas.length; i++) {
      const alarming = checkMarkerDeltaAlarming(
        parsedTrace,
        markers,
        markerDeltas[i]
      );
      if (alarming) return true;
    }
  }

  if (baseline && masks && masks.length > 0) {
    const parsedBaseline = parseTraceFromBuffer(baseline);

    for (let i = 0; i < masks.length; i++) {
      const alarming = checkSpectralMaskAlarming(
        parsedTrace,
        parsedBaseline,
        masks[i]
      );
      if (alarming) return true;
    }
  }

  return false;
};
